import { useState, useEffect, useCallback } from "react";
import { GraduationCap, Plus, Pencil, Trash2, Loader2, Users, X } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "job_readiness", label: "Job Readiness" },
  { value: "education", label: "Education Classes" },
  { value: "hospitality", label: "Hospitality" },
  { value: "certifications", label: "Certifications" },
  { value: "content_creation", label: "Content Creation" },
];

const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const emptyForm = {
  title: "", category: "education", description: "", details: "",
  vendor_name: "", vendor_contact: "", schedule: "", duration: "",
  location: "", cost: "Free", certification_offered: "",
  max_capacity: 0, requirements: "", is_active: true,
};

export const ServicesPanel = ({ getAuthHeader }) => {
  const [services, setServices] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [tab, setTab] = useState("services");

  const fetchData = useCallback(async () => {
    try {
      const [sRes, eRes] = await Promise.all([
        axios.get(`${API}/admin/services`, getAuthHeader()),
        axios.get(`${API}/admin/service-enrollments`, getAuthHeader()),
      ]);
      setServices(sRes.data);
      setEnrollments(eRes.data);
    } catch {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      title: s.title, category: s.category, description: s.description,
      details: s.details || "", vendor_name: s.vendor_name || "",
      vendor_contact: s.vendor_contact || "", schedule: s.schedule || "",
      duration: s.duration || "", location: s.location || "",
      cost: s.cost || "Free", certification_offered: s.certification_offered || "",
      max_capacity: s.max_capacity || 0, requirements: s.requirements || "",
      is_active: s.is_active !== false,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/admin/services/${editing.id}`, form, getAuthHeader());
        toast.success("Service updated");
      } else {
        await axios.post(`${API}/admin/services`, form, getAuthHeader());
        toast.success("Service created");
      }
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save service");
    }
  };

  const deleteService = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await axios.delete(`${API}/admin/services/${id}`, getAuthHeader());
      toast.success("Service deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleEnrollment = async (id) => {
    try {
      const res = await axios.put(`${API}/admin/service-enrollments/${id}/status`, {}, getAuthHeader());
      toast.success(`Enrollment ${res.data.status}`);
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteEnrollment = async (id) => {
    if (!window.confirm("Delete this enrollment?")) return;
    try {
      await axios.delete(`${API}/admin/service-enrollments/${id}`, getAuthHeader());
      toast.success("Enrollment deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-warm-600" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="services-panel">
      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button size="sm" variant={tab === "services" ? "default" : "outline"} onClick={() => setTab("services")}
            className={tab === "services" ? "bg-warm-700 text-white" : ""} data-testid="services-tab-btn">
            Programs ({services.length})
          </Button>
          <Button size="sm" variant={tab === "enrollments" ? "default" : "outline"} onClick={() => setTab("enrollments")}
            className={tab === "enrollments" ? "bg-warm-700 text-white" : ""} data-testid="enrollments-tab-btn">
            Enrollments ({enrollments.length})
          </Button>
        </div>
        {tab === "services" && (
          <Button size="sm" onClick={openCreate} className="bg-warm-700 hover:bg-warm-800 text-white gap-1" data-testid="add-service-btn">
            <Plus className="w-4 h-4" /> Add Program
          </Button>
        )}
      </div>

      {/* Services List */}
      {tab === "services" && (
        <div className="space-y-2">
          {services.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <GraduationCap className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p>No programs yet. Add your first program.</p>
            </div>
          ) : (
            services.map((s) => (
              <div key={s.id} className="border border-gray-200 rounded-xl p-4 flex items-start justify-between" data-testid={`service-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="bg-warm-100 text-warm-800 text-xs px-2 py-0.5 rounded-full">{CAT_LABEL[s.category] || s.category}</span>
                    {!s.is_active && <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">Inactive</span>}
                    {s.certification_offered && <span className="bg-sage-100 text-sage-800 text-xs px-2 py-0.5 rounded-full">{s.certification_offered}</span>}
                  </div>
                  <h4 className="font-semibold text-gray-900">{s.title}</h4>
                  <p className="text-sm text-gray-500 line-clamp-1">{s.description}</p>
                  <div className="flex gap-4 text-xs text-gray-400 mt-1">
                    {s.vendor_name && <span>Vendor: {s.vendor_name}</span>}
                    {s.schedule && <span>Schedule: {s.schedule}</span>}
                    <span><Users className="w-3 h-3 inline" /> {s.enrollment_count || 0} enrolled</span>
                  </div>
                </div>
                <div className="flex gap-1 ml-3">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteService(s.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Enrollments List */}
      {tab === "enrollments" && (
        <div className="space-y-2">
          {enrollments.length === 0 ? (
            <p className="text-center py-12 text-gray-500">No enrollments yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-gray-500 text-xs">
                  <th className="py-2 px-3">Date</th><th className="py-2 px-3">Name</th>
                  <th className="py-2 px-3">Email</th><th className="py-2 px-3">Program</th>
                  <th className="py-2 px-3">Status</th><th className="py-2 px-3">Actions</th>
                </tr></thead>
                <tbody>
                  {enrollments.map((e) => (
                    <tr key={e.id} className="border-b border-gray-100">
                      <td className="py-2 px-3 text-xs text-gray-400">{new Date(e.created_at).toLocaleDateString()}</td>
                      <td className="py-2 px-3 font-medium">{e.name}</td>
                      <td className="py-2 px-3 text-gray-500">{e.email}</td>
                      <td className="py-2 px-3">{e.service_title}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${e.status === "confirmed" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                          {e.status}
                        </span>
                      </td>
                      <td className="py-2 px-3 flex gap-1">
                        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => toggleEnrollment(e.id)}>
                          {e.status === "pending" ? "Confirm" : "Revert"}
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 h-7" onClick={() => deleteEnrollment(e.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Form Dialog */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                {editing ? "Edit Program" : "Add New Program"}
              </h3>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Title *</label>
                  <input className="w-full border rounded-lg px-3 py-2 mt-1" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Category *</label>
                  <select className="w-full border rounded-lg px-3 py-2 mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <textarea className="w-full border rounded-lg px-3 py-2 mt-1" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Details</label>
                <textarea className="w-full border rounded-lg px-3 py-2 mt-1" rows={3} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Vendor / Partner</label>
                  <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Vendor Contact</label>
                  <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.vendor_contact} onChange={(e) => setForm({ ...form, vendor_contact: e.target.value })} />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Schedule</label>
                  <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="e.g. Mon/Wed 6-8pm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Duration</label>
                  <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g. 8 weeks" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Location</label>
                  <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Cost</label>
                  <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Certification Offered</label>
                  <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.certification_offered} onChange={(e) => setForm({ ...form, certification_offered: e.target.value })} placeholder="e.g. ServSafe" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Max Capacity</label>
                  <input type="number" className="w-full border rounded-lg px-3 py-2 mt-1" value={form.max_capacity} onChange={(e) => setForm({ ...form, max_capacity: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Requirements</label>
                <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} placeholder="Prerequisites to enroll" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                <label className="text-sm text-gray-700">Active (visible to public)</label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" className="bg-warm-700 hover:bg-warm-800 text-white">{editing ? "Update" : "Create"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
